import React, { useCallback, useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Money } from "@workspace/accounting/components";
import type { Money as MoneyType } from "@workspace/accounting/types";
import { HttpRequestError } from "@workspace/base-api/fetchJson";
import { Button } from "@workspace/ui/components/button";
import { ButtonGroup } from "@workspace/ui/components/button-group";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemGroup,
	ItemHeader,
} from "@workspace/ui/components/item";

import type { OrderItem, Payment, SplitMode } from "../../types";
import type { PaymentMethods } from "../epos-context";
import { useEposContext } from "../epos-context";
import { optionRequiredOf } from "../option-required";
import { CashPaymentOptions } from "./payment-view";

// Amount Keypad Dialog for split payment
interface AmountKeypadProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (amount: number) => void;
	personLabel: string;
	currency: string;
}

function AmountKeypad({
	isOpen,
	onClose,
	onConfirm,
	personLabel,
	currency,
}: AmountKeypadProps) {
	const [amount, setAmount] = useState("");

	const handleNumberPress = useCallback((num: string) => {
		setAmount((prev) => {
			// If we're starting fresh (empty), start with the new number
			if (prev === "") return num;
			// Allow up to 7 characters total (to handle amounts like "999.99")
			if (prev.length >= 7) return prev;
			return prev + num;
		});
	}, []);

	const handleDecimal = useCallback(() => {
		setAmount((prev) => {
			// Only add decimal if not already present
			if (prev === "" || prev.includes(".")) return prev;
			return prev + ".";
		});
	}, []);

	const handleClear = useCallback(() => {
		setAmount("");
	}, []);

	const handleBackspace = useCallback(() => {
		setAmount((prev) => {
			// If only one character or empty, clear it
			if (prev.length <= 1) return "";
			return prev.slice(0, -1);
		});
	}, []);

	const handleConfirm = useCallback(async () => {
		const parsedAmount = parseFloat(amount) || 0;
		if (parsedAmount > 0) {
			// Convert decimal amount to pennies (multiply by 100 and round)
			const amountInPennies = Math.round(parsedAmount * 100);
			await onConfirm(amountInPennies);
			onClose();
		} else {
			onClose();
		}
	}, [amount, onConfirm, onClose]);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				onClose();
			}
		},
		[onClose],
	);

	// Reset amount when dialog opens
	React.useEffect(() => {
		if (isOpen) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- HA-738: react-hooks 7.1 finding, refactor deferred
			setAmount("");
		}
	}, [isOpen]);

	const keypadButtons = [
		["1", "2", "3"],
		["4", "5", "6"],
		["7", "8", "9"],
		[".", "0", "⌫"],
	];

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md" showCloseButton={false}>
				<DialogHeader>
					<DialogTitle className="text-center">Enter Amount</DialogTitle>
					<div className="text-center text-sm text-muted-foreground">
						{personLabel}
					</div>
				</DialogHeader>

				<div className="space-y-4">
					{/* Amount Display */}
					<div className="text-center">
						<div className="text-6xl font-bold text-primary p-4 bg-muted rounded-lg">
							{amount || "0"} <span className="text-3xl">{currency}</span>
						</div>
					</div>

					{/* Keypad */}
					<div className="grid grid-cols-3 gap-2">
						{keypadButtons.flat().map((key, index) => (
							<Button
								// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: a fixed keypad layout; the position is the identity
								key={index}
								variant={key === "." ? "outline" : "outline"}
								className="h-12 text-lg font-semibold"
								onClick={() => {
									if (key === "C") {
										handleClear();
									} else if (key === "⌫") {
										handleBackspace();
									} else if (key === ".") {
										handleDecimal();
									} else {
										handleNumberPress(key);
									}
								}}
							>
								{key}
							</Button>
						))}
					</div>

					{/* Action Buttons */}
					<div className="flex gap-2">
						<Button variant="outline" className="flex-1" onClick={onClose}>
							Cancel
						</Button>
						<Button className="flex-1" onClick={handleConfirm}>
							{parseFloat(amount) > 0 ? "Confirm" : "Close"}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

// Method selection buttons
function SplitMethodButtons() {
	const { splitMethod, setSplitMethod } = useEposContext();

	return (
		<div className="w-full flex justify-center">
			<ButtonGroup>
				<Button
					size="lg"
					variant={splitMethod === "items" ? "default" : "outline"}
					onClick={() =>
						setSplitMethod(splitMethod === "items" ? "none" : "items")
					}
				>
					Split by Items
				</Button>
				<Button
					size="lg"
					variant={splitMethod === "percentage" ? "default" : "outline"}
					onClick={() =>
						setSplitMethod(splitMethod === "percentage" ? "none" : "percentage")
					}
				>
					Split by %
				</Button>
				<Button
					size="lg"
					variant={splitMethod === "number" ? "default" : "outline"}
					onClick={() =>
						setSplitMethod(splitMethod === "number" ? "none" : "number")
					}
				>
					Split by Number
				</Button>
				<Button
					size="lg"
					variant={splitMethod === "amount" ? "default" : "outline"}
					onClick={() =>
						setSplitMethod(splitMethod === "amount" ? "none" : "amount")
					}
				>
					Split by Amount
				</Button>
			</ButtonGroup>
		</div>
	);
}

// Split by number of people
function SplitByNumber({ children }: React.PropsWithChildren) {
	const {
		currentOrder,
		splitCount,
		defaultCurrency,
		setOpenOrderData,
		currencies,
		initiateSplit,
		cancelSplit,
	} = useEposContext();

	// HA-607 reload survival — derive step from the persisted SplitGroup. If
	// the order already has one, the user is past configuration; show the
	// per-split rows view immediately.
	const [localStep, setLocalStep] = React.useState(0);
	const step = currentOrder?.activeSplitGroup ? 1 : localStep;
	const setStep = setLocalStep;

	const [currentSplit, setCurrentSplit] = React.useState<number | null>(null);

	const [splitPayments, setSplitPayment] = React.useState<{
		[key: number]: Payment[];
	}>({});

	// HA-607 — `setStep(1)` was a UI-only flip that left the backend unaware
	// of the split. Now we POST the definition first; the projection adds
	// `activeSplitGroup` to the order and per-split rows can read `splits[i].id`
	// to settle individual splits via card/cash with the correct splitId.
	const handleContinue = React.useCallback(async () => {
		if (!currentOrder) return;
		// If a split is already persisted (e.g. user reloaded mid-flow), skip
		// re-initiating; otherwise the backend rejects with 409 (AC3).
		if (!currentOrder.activeSplitGroup) {
			await initiateSplit(currentOrder.id, {
				mode: "Equal",
				count: splitCount,
			});
		}
		setStep(1);
	}, [currentOrder, splitCount, initiateSplit, setStep]);

	function handleCashPaymentClicked(payment: Payment) {
		setSplitPayment((prev) => {
			const updated = { ...prev };
			if (!updated[currentSplit!]) {
				updated[currentSplit!] = [];
			}
			updated[currentSplit!]!.push(payment);
			return updated;
		});
	}

	function handlePaySplit(index: number) {
		const total = currentOrder!.total.value / splitCount;

		const totalPaid =
			splitPayments[index]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		setOpenOrderData({
			...currentOrder!,
			total: currencies.money(total, defaultCurrency.code),
			subtotal: currencies.money(
				total - (currentOrder!.tax || 0) / splitCount,
				defaultCurrency.code,
			),
			change: currencies.money(change, defaultCurrency.code),
			readOnly: true,
			type: "Split",
		});

		setCurrentSplit(index);
	}

	if (!currentOrder) return null;

	if (currentSplit !== null) {
		// Calculate total for the current split
		const total = currentOrder.total.value / splitCount;

		const totalPaid =
			splitPayments[currentSplit]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;
		const outstanding = total > totalPaid ? total - totalPaid : 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		const persisted = currentOrder.activeSplitGroup?.splits[currentSplit];

		return (
			<SplitPaymentUI
				currentSplitLabel={`Person ${currentSplit + 1}`}
				outstanding={outstanding}
				totalPaid={totalPaid}
				change={change}
				splitId={persisted?.id ?? null}
				splitAmount={persisted?.amount ?? null}
				onCancel={() => {
					setSplitPayment((prev) => {
						const updated = { ...prev };
						delete updated[currentSplit];
						return updated;
					});
					setCurrentSplit(null);
				}}
				onCashPayment={handleCashPaymentClicked}
			/>
		);
	}

	if (step === 1) {
		const total = currentOrder.total.value / splitCount;

		return (
			<div className="mt-4">
				<ItemGroup className="space-y-2">
					{Array.from({ length: splitCount }).map((_, index) => (
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
						<React.Fragment key={index}>
							<Item variant="outline">
								<ItemContent>
									<ItemHeader>
										<div className="flex items-center justify-between w-full">
											<span className="font-medium">Person {index + 1}</span>
											<Money value={total} currency={currentOrder.currency} />
										</div>
									</ItemHeader>
								</ItemContent>
								<ItemActions>
									<Button
										onClick={() => {
											handlePaySplit(index);
										}}
									>
										Payment
									</Button>
								</ItemActions>
							</Item>
						</React.Fragment>
					))}
				</ItemGroup>
				<div className="p-4">
					<Button
						variant="outline"
						className="w-full"
						onClick={async () => {
							// HA-607 — when a split group is persisted, "Edit" can't
							// just flip step locally; the backend group has to be
							// cancelled first (server rejects if any split is
							// already Paid).
							if (currentOrder.activeSplitGroup) {
								await cancelSplit(currentOrder.id);
							}
							setStep(0);
						}}
					>
						Edit
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			{children}
			<div className="mt-4">
				<div className="mb-4 flex items-center justify-between border-b pb-2">
					Number of splits:
					<SplitCountControls />
				</div>
				<div className="text-sm text-muted-foreground mb-4">
					Each person pays:{" "}
					<Money
						value={(currentOrder.total.value || 0) / splitCount}
						currency={currentOrder.currency}
					/>
				</div>
				<div className="p-4">
					<Button className="w-full" onClick={handleContinue}>
						Continue
					</Button>
				</div>
			</div>
		</>
	);
}

// Split by percentage row
function SplitPercentageRow({
	index,
	percentage,
	onAdjust,
}: {
	index: number;
	percentage: number;
	onAdjust: (delta: number) => void;
}) {
	return (
		<div className="flex items-center justify-between border-b pb-2" role="row">
			<div className="flex-1">
				<div className="font-medium">Person {index + 1}</div>
				<div className="text-sm text-muted-foreground">{percentage}%</div>
			</div>
			<div className="flex items-center gap-2">
				<Button
					variant="outline"
					size="sm"
					className="size-8 p-0"
					onClick={() => onAdjust(-5)}
				>
					-
				</Button>
				<span className="mx-2 w-10 text-right text-sm font-medium">
					{percentage}%
				</span>
				<Button
					variant="outline"
					size="sm"
					className="size-8 p-0"
					onClick={() => onAdjust(5)}
				>
					+
				</Button>
			</div>
		</div>
	);
}

// Shared payment UI component for split payment
// Exported for its HA-767 test: this is where a split's cash tender is recorded.
export function SplitPaymentUI({
	currentSplitLabel,
	outstanding,
	totalPaid,
	change,
	splitId,
	splitAmount,
	onCancel,
	onCashPayment,
}: {
	currentSplitLabel: string;
	outstanding: number;
	totalPaid: number;
	change: number;
	// HA-607 — non-null when settling a specific persisted split.
	splitId?: string | null;
	splitAmount?: MoneyType | null;
	onCancel: () => void;
	onCashPayment?: (payment: Payment) => void;
}) {
	const {
		currentOrder,
		openOrderData,
		defaultCurrency,
		currencies,
		startCardPayment,
		isStartingCard,
		paymentProvider,
		recordCashPayment,
		setOpenOrderData,
		showOptionRefusal,
	} = useEposContext();

	const methods =
		(paymentProvider.paymentMethods as PaymentMethods[] | null) || [];

	// HA-767 — the cash tender is being recorded; nothing more can be tendered until it answers.
	const [isRecording, setIsRecording] = useState(false);

	if (!currentOrder) return null;

	// HA-607 — when settling a persisted split (splitId set), record the
	// cash payment server-side via the new endpoint so the projection flips
	// the matching split to Paid. Falls back to the caller's local accumulator
	// for the non-split path, which reaches the server with completeOrder.
	//
	// HA-767 — and it waits for that answer. This used to `void` the request and add the payment
	// locally at once, so the screen showed the split Paid, with change due, while the server had
	// recorded nothing; a refusal went nowhere, since the mutation has no onError.
	const handleCashPayment = async (payment: Payment) => {
		if (isRecording) return;

		if (splitId) {
			setIsRecording(true);
			try {
				await recordCashPayment(currentOrder.id, {
					paymentId: payment.id,
					amount: payment.amount,
					tip: payment.tip ?? null,
					splitId,
				});
				// HA-849 — the server now holds this tender as Completed, and a paid split freezes the
				// order: say so on the open order too, so the till offers Close Order (what the server
				// allows) and not Dismiss (which it refuses once a payment has completed).
				if (openOrderData) {
					setOpenOrderData({
						...openOrderData,
						isFrozen: true,
						payments: [
							...openOrderData.payments.filter((p) => p.id !== payment.id),
							{ ...payment, status: "Completed" },
						],
					});
				}
			} catch (error) {
				// HA-783 — Retry sends THIS tender again, same id and amount, so a retry of one that
				// did land (its answer lost) is recognised by the server instead of recorded twice.
				// The server's own reason comes first: it says when a tender is already recorded.
				// HA-877: a line still needs a required choice; retrying the tender can't help. The line's editor opens
				// over the split with the message inside it (HA-1024), and the split stays as it is.
				const missing = optionRequiredOf(error);
				if (missing) {
					showOptionRefusal(currentOrder.id, missing);
					return;
				}

				const problem =
					error instanceof HttpRequestError ? error.problem?.detail : undefined;
				toast.error("The cash payment couldn't be recorded", {
					description:
						problem ??
						(error instanceof Error ? error.message : "Unknown error"),
					action: {
						label: "Retry",
						onClick: () => void handleCashPayment(payment),
					},
				});
				return;
			} finally {
				setIsRecording(false);
			}
		}

		onCashPayment?.(payment);
	};

	return (
		<div className="mt-4">
			<Item variant="outline">
				<ItemContent>
					<ItemHeader>
						<div className="flex items-center justify-between w-full">
							<span className="font-medium">{currentSplitLabel}</span>
							<div className="flex gap-4 items-center">
								<div>
									<span className="text-sm font-medium">To Pay:</span>{" "}
									<Money value={outstanding} currency={currentOrder.currency} />
								</div>
								<div>
									<span className="text-sm font-medium">Paid:</span>{" "}
									<Money value={totalPaid} currency={currentOrder.currency} />
								</div>
								<div>
									<span className="text-sm font-medium">Change:</span>{" "}
									<Money value={change} currency={currentOrder.currency} />
								</div>
							</div>
						</div>
					</ItemHeader>
				</ItemContent>
				<ItemActions>
					<Button variant="outline" onClick={onCancel}>
						Cancel
					</Button>
				</ItemActions>
			</Item>
			<div className="flex-1 overflow-auto">
				{methods.includes("cash") && onCashPayment && (
					<div
						aria-busy={isRecording}
						className={
							isRecording ? "pointer-events-none opacity-60" : undefined
						}
					>
						{isRecording && (
							<p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
								<Loader2 className="size-4 animate-spin" />
								Recording payment…
							</p>
						)}
						<CashPaymentOptions
							order={openOrderData!}
							// HA-849 — Exact is what this split still owes, not the whole order's outstanding.
							exactAmount={{ ...currencies.money(0), value: outstanding }}
							zero={currencies.money(0)}
							onClick={(payment) => void handleCashPayment(payment)}
							currency={defaultCurrency}
							denominations={defaultCurrency.denominations?.filter(
								(x) => x.active,
							)}
						/>
					</div>
				)}

				{methods.includes("card") && (
					<div className="border-b p-4">
						<h4 className="mb-2 text-sm font-medium">Card</h4>
						<Button
							variant="default"
							className="h-12 w-full"
							disabled={isStartingCard}
							onClick={() =>
								startCardPayment(currentOrder, {
									splitId: splitId ?? null,
									amount: splitAmount ?? undefined,
								})
							}
						>
							<CreditCard className="mr-2 size-5" />
							Pay with Card
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

// Split by percentage
function SplitByPercentage({ children }: React.PropsWithChildren) {
	const {
		currentOrder,
		splitPercentages,
		setSplitPercentages,
		defaultCurrency,
		setOpenOrderData,
		currencies,
		initiateSplit,
		cancelSplit,
	} = useEposContext();

	// HA-607 reload survival — see SplitByNumber for the rationale.
	const [localStep, setLocalStep] = React.useState(0);
	const step = currentOrder?.activeSplitGroup ? 1 : localStep;
	const setStep = setLocalStep;

	const [currentSplit, setCurrentSplit] = React.useState<number | null>(null);
	const [splitPayments, setSplitPayment] = React.useState<{
		[key: number]: Payment[];
	}>({});

	const handleContinue = React.useCallback(async () => {
		if (!currentOrder) return;
		if (!currentOrder.activeSplitGroup) {
			await initiateSplit(currentOrder.id, {
				mode: "Percent",
				count: splitPercentages.length,
				percentages: splitPercentages,
			});
		}
		setStep(1);
	}, [currentOrder, splitPercentages, initiateSplit, setStep]);

	if (!currentOrder) return null;

	const handleAddPerson = () => {
		const newPercentages = [...splitPercentages];
		const avgPercentage = 100 / (newPercentages.length + 1);
		setSplitPercentages(
			Array(newPercentages.length + 1)
				.fill(0)
				.map(() => Math.round(avgPercentage)),
		);
	};

	const handleRemovePerson = () => {
		if (splitPercentages.length <= 2) return;

		const newPercentages = splitPercentages.slice(0, -1);
		const avgPercentage = 100 / newPercentages.length;
		setSplitPercentages(newPercentages.map(() => Math.round(avgPercentage)));
	};

	const handleAdjustPercentage = (index: number, delta: number) => {
		const newPercentages = [...splitPercentages];
		const otherIndex = (index + 1) % splitPercentages.length;

		newPercentages[index] = Math.max(
			0,
			Math.min(100, newPercentages[index]! + delta),
		);
		newPercentages[otherIndex] = 100 - newPercentages[index];

		setSplitPercentages(newPercentages);
	};

	function handleCashPaymentClicked(payment: Payment) {
		setSplitPayment((prev) => {
			const updated = { ...prev };
			if (!updated[currentSplit!]) {
				updated[currentSplit!] = [];
			}
			updated[currentSplit!]!.push(payment);
			return updated;
		});
	}

	function handlePaySplit(index: number) {
		const total = (currentOrder!.total.value * splitPercentages[index]!) / 100;

		const totalPaid =
			splitPayments[index]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		setOpenOrderData({
			...currentOrder!,
			total: currencies.money(total, defaultCurrency.code),
			subtotal: currencies.money(
				total - ((currentOrder!.tax || 0) * splitPercentages[index]!) / 100,
				defaultCurrency.code,
			),
			change: currencies.money(change, defaultCurrency.code),
			readOnly: true,
			type: "Split",
		});

		setCurrentSplit(index);
	}

	if (currentSplit !== null) {
		// Calculate total for the current split
		const total =
			(currentOrder.total.value * splitPercentages[currentSplit]!) / 100;

		const totalPaid =
			splitPayments[currentSplit]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;
		const outstanding = total > totalPaid ? total - totalPaid : 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		const persisted = currentOrder.activeSplitGroup?.splits[currentSplit];

		return (
			<SplitPaymentUI
				currentSplitLabel={`Person ${currentSplit + 1} (${splitPercentages[currentSplit]}%)`}
				outstanding={outstanding}
				totalPaid={totalPaid}
				change={change}
				splitId={persisted?.id ?? null}
				splitAmount={persisted?.amount ?? null}
				onCancel={() => {
					setSplitPayment((prev) => {
						const updated = { ...prev };
						delete updated[currentSplit];
						return updated;
					});
					setCurrentSplit(null);
				}}
				onCashPayment={handleCashPaymentClicked}
			/>
		);
	}

	if (step === 1) {
		return (
			<div className="mt-4">
				<ItemGroup className="space-y-2">
					{splitPercentages.map((percentage, index) => (
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
						<React.Fragment key={index}>
							<Item variant="outline">
								<ItemContent>
									<ItemHeader>
										<div className="flex items-center justify-between w-full">
											<span className="font-medium">Person {index + 1}</span>
											<div className="flex items-center gap-2">
												<span className="text-sm text-muted-foreground">
													{percentage}%
												</span>
												<Money
													value={(currentOrder.total.value * percentage) / 100}
													currency={currentOrder.currency}
												/>
											</div>
										</div>
									</ItemHeader>
								</ItemContent>
								<ItemActions>
									<Button
										onClick={() => {
											handlePaySplit(index);
										}}
									>
										Payment
									</Button>
								</ItemActions>
							</Item>
						</React.Fragment>
					))}
				</ItemGroup>
				<div className="p-4">
					<Button
						variant="outline"
						className="w-full"
						onClick={async () => {
							// HA-607 — when a split group is persisted, "Edit" can't
							// just flip step locally; the backend group has to be
							// cancelled first (server rejects if any split is
							// already Paid).
							if (currentOrder.activeSplitGroup) {
								await cancelSplit(currentOrder.id);
							}
							setStep(0);
						}}
					>
						Edit
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			{children}
			<div className="mt-4">
				<div className="mb-4 flex items-center justify-between border-b pb-2">
					Adjust percentages:
					<SplitCountControls
						onAdd={handleAddPerson}
						onRemove={handleRemovePerson}
					/>
				</div>
				<div className="space-y-2">
					{splitPercentages.map((percentage, index) => (
						<SplitPercentageRow
							// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
							key={index}
							index={index}
							percentage={percentage}
							onAdjust={(delta) => handleAdjustPercentage(index, delta)}
						/>
					))}
				</div>
				<div className="p-2 text-sm text-muted-foreground border-t pt-4 mb-4">
					<div className="flex justify-between">
						<span>Order Total:</span>
						<Money
							value={currentOrder.total.value}
							currency={currentOrder.currency}
						/>
					</div>
				</div>
				<div className="p-4">
					<Button className="w-full" onClick={handleContinue}>
						Continue
					</Button>
				</div>
			</div>
		</>
	);
}

// Split by amount
function SplitByAmount({ children }: React.PropsWithChildren) {
	const {
		currentOrder,
		splitCount,
		setSplitCount,
		defaultCurrency,
		setOpenOrderData,
		currencies,
		initiateSplit,
		cancelSplit,
	} = useEposContext();

	// HA-607 reload survival — see SplitByNumber for the rationale.
	const [localStep, setLocalStep] = React.useState(0);
	const step = currentOrder?.activeSplitGroup ? 1 : localStep;
	const setStep = setLocalStep;

	const [currentSplit, setCurrentSplit] = React.useState<number | null>(null);
	const [keypadOpen, setKeypadOpen] = React.useState(false);
	const [keypadEditingIndex, setKeypadEditingIndex] = React.useState<
		number | null
	>(null);

	const [splitPayments, setSplitPayment] = React.useState<{
		[key: number]: Payment[];
	}>({});

	const [splitAmounts, setSplitAmounts] = React.useState<{
		[key: number]: number;
	}>({});

	const handleContinue = React.useCallback(async () => {
		if (!currentOrder) return;
		if (!currentOrder.activeSplitGroup) {
			const amounts: MoneyType[] = Array.from(
				{ length: splitCount },
				(_, i) => ({
					value: splitAmounts[i] ?? 0,
					currency: currentOrder.currency,
					version: 2,
				}),
			);
			await initiateSplit(currentOrder.id, {
				mode: "Amounts",
				count: splitCount,
				amountsPerSplit: amounts,
			});
		}
		setStep(1);
	}, [currentOrder, splitCount, splitAmounts, initiateSplit, setStep]);

	function handleCashPaymentClicked(payment: Payment) {
		setSplitPayment((prev) => {
			const updated = { ...prev };
			if (!updated[currentSplit!]) {
				updated[currentSplit!] = [];
			}
			updated[currentSplit!]!.push(payment);
			return updated;
		});
	}

	function handlePaySplit(index: number) {
		const total = splitAmounts[index] || 0;

		const totalPaid =
			splitPayments[index]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		setOpenOrderData({
			...currentOrder!,
			total: currencies.money(total, defaultCurrency.code),
			subtotal: currencies.money(total, defaultCurrency.code),
			change: currencies.money(change, defaultCurrency.code),
			readOnly: true,
			type: "Split",
		});

		setCurrentSplit(index);
	}

	const handleAmountConfirm = (amount: number) => {
		if (keypadEditingIndex !== null) {
			const newAmounts = { ...splitAmounts };
			newAmounts[keypadEditingIndex] = amount;
			setSplitAmounts(newAmounts);
		}
		setKeypadOpen(false);
		setKeypadEditingIndex(null);
	};

	if (!currentOrder) return null;

	const totalAllocated = Object.values(splitAmounts).reduce(
		(sum, amount) => sum + amount,
		0,
	);
	const isValid =
		totalAllocated > 0 && Object.keys(splitAmounts).length === splitCount;

	if (currentSplit !== null) {
		// Calculate total for the current split
		const total = splitAmounts[currentSplit] || 0;

		const totalPaid =
			splitPayments[currentSplit]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;
		const outstanding = total > totalPaid ? total - totalPaid : 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		const persistedAmount = currentOrder.activeSplitGroup?.splits[currentSplit];

		return (
			<SplitPaymentUI
				currentSplitLabel={`Person ${currentSplit + 1}`}
				outstanding={outstanding}
				totalPaid={totalPaid}
				change={change}
				splitId={persistedAmount?.id ?? null}
				splitAmount={persistedAmount?.amount ?? null}
				onCancel={() => {
					setSplitPayment((prev) => {
						const updated = { ...prev };
						delete updated[currentSplit];
						return updated;
					});
					setCurrentSplit(null);
				}}
				onCashPayment={handleCashPaymentClicked}
			/>
		);
	}

	if (step === 1) {
		return (
			<div className="mt-4">
				<ItemGroup className="space-y-2">
					{Array.from({ length: splitCount }).map((_, index) => (
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
						<React.Fragment key={index}>
							<Item variant="outline">
								<ItemContent>
									<ItemHeader>
										<div className="flex items-center justify-between w-full">
											<span className="font-medium">Person {index + 1}</span>
											<Money
												value={splitAmounts[index] || 0}
												currency={currentOrder.currency}
											/>
										</div>
									</ItemHeader>
								</ItemContent>
								<ItemActions>
									<Button
										onClick={() => {
											handlePaySplit(index);
										}}
										disabled={(splitAmounts[index] || 0) <= 0}
									>
										Payment
									</Button>
								</ItemActions>
							</Item>
						</React.Fragment>
					))}
				</ItemGroup>
				<div className="mt-4 p-2 text-sm text-muted-foreground border-t pt-4">
					<div className="flex justify-between mb-2">
						<span>Total Allocated:</span>
						<Money value={totalAllocated} currency={currentOrder.currency} />
					</div>
					<div className="flex justify-between">
						<span>Order Total:</span>
						<Money
							value={currentOrder.total.value}
							currency={currentOrder.currency}
						/>
					</div>
				</div>
				<div className="p-4">
					<Button
						variant="outline"
						className="w-full"
						onClick={async () => {
							// HA-607 — when a split group is persisted, "Edit" can't
							// just flip step locally; the backend group has to be
							// cancelled first (server rejects if any split is
							// already Paid).
							if (currentOrder.activeSplitGroup) {
								await cancelSplit(currentOrder.id);
							}
							setStep(0);
						}}
					>
						Edit
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			{children}
			<div className="mt-4">
				<div className="mb-4 flex items-center justify-between border-b pb-2">
					Number of people:
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							className="size-8 p-0"
							onClick={() => setSplitCount(Math.max(2, splitCount - 1))}
							disabled={splitCount <= 2}
						>
							-
						</Button>
						<span className="text-sm">{splitCount} people</span>
						<Button
							variant="outline"
							className="size-8 p-0"
							onClick={() => setSplitCount(splitCount + 1)}
						>
							+
						</Button>
					</div>
				</div>
				<div className="space-y-2 mb-4">
					{Array.from({ length: splitCount }).map((_, index) => (
						<SplitAmountRow
							// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
							key={index}
							index={index}
							amount={splitAmounts[index] || 0}
							onEdit={() => {
								setKeypadEditingIndex(index);
								setKeypadOpen(true);
							}}
							currency={defaultCurrency.code}
						/>
					))}
				</div>
				<div className="p-2 text-sm text-muted-foreground border-t pt-4 mb-4">
					<div className="flex justify-between mb-2">
						<span>Total Allocated:</span>
						<Money value={totalAllocated} currency={currentOrder.currency} />
					</div>
					<div className="flex justify-between">
						<span>Order Total:</span>
						<Money
							value={currentOrder.total.value}
							currency={currentOrder.currency}
						/>
					</div>
				</div>
				<div className="p-4">
					<Button
						className="w-full"
						onClick={handleContinue}
						disabled={!isValid}
					>
						Continue
					</Button>
				</div>
			</div>
			<AmountKeypad
				isOpen={keypadOpen}
				onClose={() => {
					setKeypadOpen(false);
					setKeypadEditingIndex(null);
				}}
				onConfirm={handleAmountConfirm}
				personLabel={
					keypadEditingIndex !== null ? `Person ${keypadEditingIndex + 1}` : ""
				}
				currency={defaultCurrency.code}
			/>
		</>
	);
}

// Split count adjustment controls
function SplitCountControls({
	onAdd,
	onRemove,
}: {
	onAdd?: () => void;
	onRemove?: () => void;
}) {
	const { splitCount, setSplitCount } = useEposContext();

	return (
		<div className="flex items-center gap-2">
			<Button
				variant="outline"
				className="size-8 p-0"
				onClick={() => {
					setSplitCount(splitCount - 1);
					onRemove?.();
				}}
				disabled={splitCount <= 2}
			>
				-
			</Button>

			<span className="text-sm">{splitCount} people</span>

			<Button
				variant="outline"
				className="size-8 p-0"
				onClick={() => {
					setSplitCount(splitCount + 1);
					onAdd?.();
				}}
			>
				+
			</Button>
		</div>
	);
}

// Split by amount row
function SplitAmountRow({
	index,
	amount,
	onEdit,
	currency,
}: {
	index: number;
	amount: number;
	onEdit: () => void;
	currency: string;
}) {
	// Convert pennies to decimal for display
	const displayAmount = (amount / 100).toFixed(2);

	return (
		<div className="flex items-center justify-between border-b pb-2" role="row">
			<div className="flex-1">
				<div className="font-medium">Person {index + 1}</div>
			</div>
			<Button variant="outline" className="gap-2" onClick={onEdit}>
				{amount > 0 ? (
					<>
						<span>{displayAmount}</span>
						<span className="text-muted-foreground">{currency}</span>
					</>
				) : (
					<span className="text-muted-foreground">Set Amount</span>
				)}
			</Button>
		</div>
	);
}

// Split by individual items
function SplitItemRow({
	item,
	splitCount,
}: {
	item: OrderItem;
	splitCount: number;
}) {
	const { splitItems, setSplitItems } = useEposContext();

	return (
		<div
			key={item.id}
			className="flex items-center justify-between border-b pb-2"
			role="row"
		>
			<div className="flex-1">
				<div className="font-medium">{item.name}</div>
				<div className="text-sm">
					<Money {...item.price} />
				</div>
			</div>
			<div className="flex items-center">
				{Array.from({ length: splitCount }).map((_, index) => (
					<Button
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
						key={index}
						variant={splitItems[item.id] === index + 1 ? "default" : "outline"}
						className="mr-1 size-8 p-0"
						onClick={() => {
							const newSplitItems = { ...splitItems };
							newSplitItems[item.id] = index + 1;
							setSplitItems(newSplitItems);
						}}
					>
						{index + 1}
					</Button>
				))}
			</div>
		</div>
	);
}

// Split by individual items
function SplitByItems({ children }: React.PropsWithChildren) {
	const {
		currentOrder,
		splitCount,
		splitItems,
		defaultCurrency,
		setOpenOrderData,
		currencies,
		initiateSplit,
		cancelSplit,
	} = useEposContext();

	// HA-607 reload survival — see SplitByNumber for the rationale.
	const [localStep, setLocalStep] = React.useState(0);
	const step = currentOrder?.activeSplitGroup ? 1 : localStep;
	const setStep = setLocalStep;

	const [currentSplit, setCurrentSplit] = React.useState<number | null>(null);

	const [splitPayments, setSplitPayment] = React.useState<{
		[key: number]: Payment[];
	}>({});

	const handleContinue = React.useCallback(async () => {
		if (!currentOrder) return;
		if (!currentOrder.activeSplitGroup) {
			// splitItems maps itemId → 1-based split index. Convert to the
			// itemIdsPerSplit shape the backend SplitDefinition expects.
			const buckets: string[][] = Array.from({ length: splitCount }, () => []);
			for (const [itemId, splitIndex] of Object.entries(splitItems)) {
				if (splitIndex >= 1 && splitIndex <= splitCount) {
					buckets[splitIndex - 1]!.push(itemId);
				}
			}
			await initiateSplit(currentOrder.id, {
				mode: "Items",
				count: splitCount,
				itemIdsPerSplit: buckets,
			});
		}
		setStep(1);
	}, [currentOrder, splitCount, splitItems, initiateSplit, setStep]);

	function handleCashPaymentClicked(payment: Payment) {
		setSplitPayment((prev) => {
			const updated = { ...prev };
			if (!updated[currentSplit!]) {
				updated[currentSplit!] = [];
			}
			updated[currentSplit!]!.push(payment);
			return updated;
		});
	}

	function handlePaySplit(index: number) {
		const total =
			currentOrder?.items.reduce((acc, item) => {
				const assignedSplit = splitItems[item.id];
				if (assignedSplit === index + 1) {
					return acc + (item.price.value || 0);
				}
				return acc;
			}, 0) || 0;

		const totalPaid =
			splitPayments[index]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		setOpenOrderData({
			...currentOrder!,
			total: currencies.money(total, defaultCurrency.code),
			subtotal: currencies.money(
				total - (currentOrder!.tax || 0),
				defaultCurrency.code,
			),
			change: currencies.money(change, defaultCurrency.code),
			readOnly: true,
			type: "Split",
		});

		setCurrentSplit(index);
	}

	if (!currentOrder) return null;

	const valid = Object.keys(splitItems).length === currentOrder.items.length;

	if (currentSplit !== null) {
		// Calculate total for the current split
		const total = currentOrder.items.reduce((acc, item) => {
			const assignedSplit = splitItems[item.id];
			if (assignedSplit === currentSplit + 1) {
				return acc + (item.price.value || 0);
			}
			return acc;
		}, 0);

		const totalPaid =
			splitPayments[currentSplit]?.reduce(
				(sum, payment) => sum + payment.amount.value,
				0,
			) || 0;
		const outstanding = total > totalPaid ? total - totalPaid : 0;

		const change = totalPaid > total ? totalPaid - total : 0;

		const persistedItemSplit =
			currentOrder.activeSplitGroup?.splits[currentSplit];

		return (
			<SplitPaymentUI
				currentSplitLabel={`Person ${currentSplit + 1}`}
				outstanding={outstanding}
				totalPaid={totalPaid}
				change={change}
				splitId={persistedItemSplit?.id ?? null}
				splitAmount={persistedItemSplit?.amount ?? null}
				onCancel={() => {
					setSplitPayment((prev) => {
						const updated = { ...prev };
						delete updated[currentSplit];
						return updated;
					});
					setCurrentSplit(null);
				}}
				onCashPayment={handleCashPaymentClicked}
			/>
		);
	}

	if (step === 1) {
		const totals = Array.from({ length: splitCount }).map(() => 0);
		currentOrder.items.forEach((item) => {
			const assignedSplit = splitItems[item.id];
			if (assignedSplit) {
				totals[assignedSplit - 1]! += item.price.value || 0;
			}
		});

		const totalAllocated = totals.reduce((sum, total) => sum + total, 0);

		return (
			<div className="mt-4">
				<ItemGroup className="space-y-2">
					{totals.map((total, index) => (
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
						<React.Fragment key={index}>
							<Item variant="outline">
								<ItemContent>
									<ItemHeader>
										<div className="flex items-center justify-between w-full">
											<span className="font-medium">Person {index + 1}</span>
											<Money value={total} currency={currentOrder.currency} />
										</div>
									</ItemHeader>
								</ItemContent>
								<ItemActions>
									<Button
										onClick={() => {
											handlePaySplit(index);
										}}
									>
										Payment
									</Button>
								</ItemActions>
							</Item>
						</React.Fragment>
					))}
				</ItemGroup>
				<div className="mt-4 p-2 text-sm text-muted-foreground border-t pt-4">
					<div className="flex justify-between mb-2">
						<span>Total Allocated:</span>
						<Money value={totalAllocated} currency={currentOrder.currency} />
					</div>
					<div className="flex justify-between">
						<span>Order Total:</span>
						<Money
							value={currentOrder.total.value}
							currency={currentOrder.currency}
						/>
					</div>
				</div>
				<div className="p-4">
					<Button
						variant="outline"
						className="w-full"
						onClick={async () => {
							// HA-607 — when a split group is persisted, "Edit" can't
							// just flip step locally; the backend group has to be
							// cancelled first (server rejects if any split is
							// already Paid).
							if (currentOrder.activeSplitGroup) {
								await cancelSplit(currentOrder.id);
							}
							setStep(0);
						}}
					>
						Edit
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			{children}
			<div className="mt-4">
				<div className="mb-4 flex items-center justify-between border-b pb-2">
					Select items for each person:
					<SplitCountControls />
				</div>
				<div className="space-y-2">
					{currentOrder.items.map((item) => (
						<SplitItemRow key={item.id} item={item} splitCount={splitCount} />
					))}
				</div>
				<div className="p-2 text-sm text-muted-foreground border-t pt-4 mb-4">
					<div className="mb-3 pb-2 border-b">
						<span className="font-medium text-foreground">
							Allocation Summary:
						</span>
					</div>
					{Array.from({ length: splitCount }).map((_, personIndex) => {
						const personTotal = currentOrder.items.reduce((sum, item) => {
							const assignedSplit = splitItems[item.id];
							if (assignedSplit === personIndex + 1) {
								return sum + (item.price.value || 0);
							}
							return sum;
						}, 0);

						return (
							<div
								// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: the index is the identity here (split / slot N), and rows are never reordered or removed from the middle
								key={personIndex}
								className="flex justify-between items-center mb-2"
							>
								<span>Person {personIndex + 1}:</span>
								<Money value={personTotal} currency={currentOrder.currency} />
							</div>
						);
					})}
					<div className="flex justify-between pt-2 border-t mt-2 font-medium">
						<span>Order Total:</span>
						<Money
							value={currentOrder.total.value}
							currency={currentOrder.currency}
						/>
					</div>
				</div>
				<div className="p-4">
					<Button className="w-full" disabled={!valid} onClick={handleContinue}>
						Continue
					</Button>
				</div>
			</div>
		</>
	);
}

// HA-607 — map persisted SplitGroup.Definition.Mode (backend) to the UI's
// `splitMethod` string. When an order has an active split group, the mode the
// UI renders is derived from the persisted definition, not from local React
// state — this is what makes mid-flow reload survive.
function persistedModeToUi(
	mode: SplitMode,
): "items" | "percentage" | "number" | "amount" {
	switch (mode) {
		case "Items":
			return "items";
		case "Percent":
			return "percentage";
		case "Amounts":
			return "amount";
		case "Equal":
		default:
			return "number";
	}
}

// Main split payment component
export function SplitPayment() {
	const { currentOrder, splitMethod } = useEposContext();

	if (!currentOrder) return null;

	const header = (
		<>
			<h4 className="mb-2 text-sm font-medium">Split Payment</h4>
			<SplitMethodButtons />
		</>
	);

	// Persisted state wins on reload — derive the UI mode from the order's
	// ActiveSplitGroup so the user lands back on the rows view (with their
	// in-flight payment progress intact) rather than the config screen.
	const persisted = currentOrder.activeSplitGroup?.definition.mode;
	const activeMethod = persisted ? persistedModeToUi(persisted) : splitMethod;

	return (
		<div className="border-b p-4">
			{activeMethod === "none" && header}

			{activeMethod === "number" && <SplitByNumber>{header}</SplitByNumber>}
			{activeMethod === "percentage" && (
				<SplitByPercentage>{header}</SplitByPercentage>
			)}
			{activeMethod === "amount" && <SplitByAmount>{header}</SplitByAmount>}
			{activeMethod === "items" && <SplitByItems>{header}</SplitByItems>}
		</div>
	);
}
